import { type FC, useEffect, useRef, useState } from "react";
import { Platform, Pressable, View } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import "../../theme";
import { Text } from "../Text";
import { Checkbox } from "../Checkbox";
import type { MultiOptionState } from "./types";

interface MultiSelectOptionProps {
  readonly label: string;
  readonly state: MultiOptionState;
  readonly onPress: () => void;
  readonly onHoverIn?: (() => void) | undefined;
}

const MultiSelectOption: FC<MultiSelectOptionProps> = ({ label, state, onPress, onHoverIn }) => {
  const [hovered, setHovered] = useState(false);
  const ref = useRef<React.ComponentRef<typeof Pressable>>(null);

  useEffect(() => {
    if (state.focused && Platform.OS === "web" && ref.current) {
      (ref.current as unknown as HTMLElement).scrollIntoView({ block: "nearest" });
    }
  }, [state.focused]);

  return (
    <Pressable
      ref={ref}
      onPress={onPress}
      onHoverIn={() => {
        setHovered(true);
        onHoverIn?.();
      }}
      onHoverOut={() => setHovered(false)}
      style={[styles.option, (state.focused || hovered) && styles.optionFocused]}
    >
      <View pointerEvents="none">
        <Checkbox checked={state.selected} size={18} />
      </View>
      <Text textStyle="sm/regular" color="text" numberOfLines={1} style={styles.label}>
        {label}
      </Text>
    </Pressable>
  );
};

MultiSelectOption.displayName = "MultiSelectOption";

export { MultiSelectOption };

const styles = StyleSheet.create((theme) => ({
  option: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.md,
    paddingHorizontal: theme.spacing["3xl"],
    paddingVertical: theme.spacing.xl,
    userSelect: "none",
  },
  optionFocused: {
    backgroundColor: theme.colors.surface2.hover,
  },
  label: {
    flex: 1,
  },
}));
